/**
 * The splat mesh's place in the APP'S frame.
 *
 * `ThreeSplatBridge` fills one `GaussianSplat` — the avatar's lifted slots and
 * the environment's static tail — and no longer draws anything. This mounts it
 * in the app's scene and gives it the two moments it needs:
 *
 *   BEFORE the render, the frame's compute: the environment sync, the model
 *   matrix the lift reads, the see-through pass and the stable sort. A
 *   `useFrame` at priority 0 runs ahead of R3F's own render, and the compute and
 *   the raster land in that order in one queue, which IS the ordering guarantee
 *   — the sort writes the order buffer the raster reads.
 *
 *   AFTER the render, the backpressure: `scene.onAfterRender` releases whoever
 *   was waiting on `nextFrame()`. `headTeethEngine` awaits that inside its mutex
 *   after every present, and with no readback in the frame nothing else bounds
 *   the GPU queue — without it the decode loop runs ahead by tens of frames and
 *   the page's own frames starve (measured: 9 fps while orbiting at 17 poses/s).
 *
 * ONLY THE VIEW'S OWN RENDER COUNTS. three re-enters `render()` for the sun's
 * VSM shadow map, on the SAME scene with the LIGHT's camera, so the hook fires
 * twice per frame (measured: 150 releases against 75 frames) and the waiters
 * would be released on a pass that draws no splats — the decode loop then runs
 * half a frame ahead of the screen for no reason. The camera is what tells them
 * apart, and it is the honest test: the promise is about the frame the VIEWER
 * sees.
 *
 * THE SORT STAYS DIRTY-DRIVEN while the raster is now unconditional, and the
 * split matters: the sort is the expensive half (36.6 ms with an environment
 * against 4.0 without — docs/OGS_ARCHITECTURE.md §10) and only a moved splat or
 * a moved camera can invalidate it. Dragging a gizmo does neither.
 *
 * `onAfterRender` is the SCENE's, not the mesh's: a mesh hook fires per draw
 * call, inside the render, and would release the waiters before the frame it is
 * promising. It is also assigned rather than added, so a second mount replacing
 * the first cannot leave the old one wired.
 */
import { useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'

export default function SplatSceneMesh({ driver }) {
  const scene = useThree(s => s.scene)
  const camera = useThree(s => s.camera)

  useEffect(() => {
    if (!driver) return undefined
    const prev = scene.onAfterRender
    scene.onAfterRender = function onAfterRender(renderer, renderedScene, renderedCamera, ...rest) {
      prev?.apply(this, [renderer, renderedScene, renderedCamera, ...rest])
      if (renderedCamera === camera) driver.presented()
    }
    return () => { scene.onAfterRender = prev }
  }, [driver, scene, camera])

  useFrame(() => { driver?.beginAppFrame() })

  return null
}
